import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/sections/Footer';
import { TextLink } from '../components/sections/TextLink';
import api from '../lib/api';
import { responsiveImg } from '../lib/imageUrl';
import { useText } from '../lib/siteContent';
import { categoryLabel } from '../lib/projects';

const Kicker = ({ children }) => <h2 className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#111111] border-b border-[#111111]/10 pb-4 mb-8">{children}</h2>;

const List = ({ items, testId }) => (
  <ul className="space-y-3" data-testid={testId}>
    {items.map((x) => <li key={x} className="text-sm md:text-base font-light text-[#333333] leading-[1.8]">{x}</li>)}
  </ul>
);

const Palette = ({ colors }) => (
  <ul className="flex flex-wrap gap-x-8 gap-y-6" data-testid="project-palette">
    {colors.map((c) => (
      <li key={`${c.hex}-${c.name}`} className="flex items-center gap-3">
        <span className="w-10 h-10 border border-[#111111]/15 shrink-0" style={{ backgroundColor: c.hex }} aria-hidden="true" />
        <span className="text-sm font-light text-[#111111]">{c.name}</span>
      </li>
    ))}
  </ul>
);

export default function ProjectPage() {
  const { slug } = useParams();
  const t = useText();
  const [p, setP] = useState(undefined);

  useEffect(() => {
    api.get(`/projects/${slug}`).then(({ data }) => { setP(data); document.title = `${data.title} | JBMH Events`; }).catch(() => setP(null));
  }, [slug]);

  if (p === null) return <Navigate to="/realisations" replace />;
  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#111111]" data-testid="project-page">
      <Navbar />
      {p && (
        <main className="pb-32">
          <section className="container-custom pt-20 md:pt-32 pb-16 md:pb-20">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
              <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-8" data-testid="project-category">{categoryLabel(t, p.category)}</span>
              <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-5xl break-words" data-testid="project-title">{p.title}</h1>
              {p.location && <p className="mt-8 text-sm md:text-base font-light text-[#333333]" data-testid="project-location">{p.location}</p>}
            </motion.div>
          </section>
          {p.coverUrl && (
            <div className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] overflow-hidden bg-[#F5F5F5]">
              <img {...responsiveImg(p.coverUrl, '100vw', { eager: true })} alt={p.title} className="w-full h-full object-cover" data-testid="project-cover" />
            </div>
          )}
          <section className="container-custom pt-24 md:pt-32 grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-16 lg:gap-24">
            <div>
              <h2 className="font-serif font-light text-[#111111] text-3xl md:text-4xl leading-[1.15]">{t('realpage.project.kicker')}</h2>
              {p.intro && <p className="mt-8 text-sm md:text-base leading-[1.9] font-light text-[#333333] whitespace-pre-line" data-testid="project-intro">{p.intro}</p>}
            </div>
            <div className="space-y-16">
              {p.direction && <div><Kicker>{t('realpage.project.direction')}</Kicker><p className="text-sm md:text-base leading-[1.9] font-light text-[#333333] whitespace-pre-line" data-testid="project-direction">{p.direction}</p></div>}
              {p.palette?.length > 0 && <div><Kicker>{t('realpage.project.palette')}</Kicker><Palette colors={p.palette} /></div>}
              {p.spaces?.length > 0 && <div><Kicker>{t('realpage.project.spaces')}</Kicker><List items={p.spaces} testId="project-spaces" /></div>}
              {p.partners?.length > 0 && <div><Kicker>{t('realpage.project.partners')}</Kicker><List items={p.partners} testId="project-partners" /></div>}
            </div>
          </section>
          {p.images?.length > 0 && (
            <section className="pt-24 md:pt-36 space-y-4 md:space-y-6" data-testid="project-gallery">
              {p.images.map((src, i) => (
                <motion.img key={`${src}-${i}`} {...responsiveImg(src, '(max-width: 768px) 100vw, 50vw')} alt={`${p.title} — image ${i + 1}`}
                  initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 1 }}
                  className="w-full h-auto block" />
              ))}
            </section>
          )}
          <section className="container-custom pt-24 md:pt-32 flex flex-col sm:flex-row gap-10 sm:gap-16">
            <TextLink to="/realisations" testId="project-back">{t('realpage.project.back')}</TextLink>
            <TextLink to="/devis" testId="project-cta-devis">{t('realpage.project.cta')}</TextLink>
          </section>
        </main>
      )}
      <Footer />
    </div>
  );
}
